const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

function getAuthorizationHeader(endpoint: string): string | null {
  try {
    const userType = localStorage.getItem('userType');

    if (userType === 'publisher') {
      const publisherToken = localStorage.getItem('publisherToken');
      if (
        publisherToken &&
        (endpoint === '/api/publisher/me' ||
          endpoint.startsWith('/api/publisher/') ||
          endpoint.startsWith('/api/publishers/'))
      ) {
        return `Bearer ${publisherToken}`;
      }
      return null;
    }

    const storedToken = localStorage.getItem('authToken');
    if (userType === 'admin' && storedToken) {
      return `Bearer ${storedToken}`;
    }
  } catch {
    // ignore (e.g. SSR / storage not available)
  }

  // Optional fallback for local development only (do not rely on this in production).
  const envToken = process.env.REACT_APP_ADMIN_TOKEN;
  if (envToken) {
    return `Bearer ${envToken}`;
  }

  if (process.env.NODE_ENV === 'development') {
    return 'Bearer 1wax!WAX';
  }

  return null;
}

class ApiService {
  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const authorizationFromCaller =
      options.headers && typeof options.headers === 'object'
        ? (options.headers as Record<string, string>)['Authorization']
        : undefined;

    const authHeader = authorizationFromCaller || getAuthorizationHeader(endpoint);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.message || 'Request failed');
    }

    return response.json();
  }

  // Advertisers API
  async getAdvertisers(params?: { limit?: number; page?: number; skip?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/advertisers${queryString ? `?${queryString}` : ''}`);
  }

  async getAdvertiser(id: number) {
    return this.request(`/api/advertisers/${id}`);
  }

  async createAdvertiser(data: any) {
    return this.request('/api/advertisers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdvertiser(id: number, data: any) {
    return this.request(`/api/advertisers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAdvertiser(id: number) {
    return this.request(`/api/advertisers/${id}`, {
      method: 'DELETE',
    });
  }

  // Publishers API
  async getPublisherMe() {
    return this.request('/api/publisher/me');
  }

  async getPublishers(params?: { limit?: number; page?: number; skip?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/publishers${queryString ? `?${queryString}` : ''}`);
  }

  async getPublisher(id: number) {
    return this.request(`/api/publishers/${id}`);
  }

  async createPublisher(data: any) {
    return this.request('/api/publishers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePublisher(id: number, data: any) {
    return this.request(`/api/publishers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePublisher(id: number) {
    return this.request(`/api/publishers/${id}`, {
      method: 'DELETE',
    });
  }

  async getPublisherAdvertiserApprovals(publisherId: number) {
    return this.request(`/api/publishers/${publisherId}/advertiser-approvals`);
  }

  async getAdvertiserPublishers(advertiserId: number) {
    return this.request(`/api/advertisers/${advertiserId}/publishers`);
  }

  async approvePublisherForAdvertiser(advertiserId: number, publisherId: number) {
    return this.request(`/api/advertisers/${advertiserId}/publishers/${publisherId}/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async blockPublisherForAdvertiser(advertiserId: number, publisherId: number) {
    return this.request(`/api/advertisers/${advertiserId}/publishers/${publisherId}/block`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Offers API
  async getOffers(params?: { limit?: number; page?: number; skip?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/offers${queryString ? `?${queryString}` : ''}`);
  }

  async getOffer(id: number) {
    return this.request(`/api/offers/${id}`);
  }

  async createOffer(data: any) {
    return this.request('/api/offers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOffer(id: number, data: any) {
    return this.request(`/api/offers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteOffer(id: number) {
    return this.request(`/api/offers/${id}`, {
      method: 'DELETE',
    });
  }

  // Analytics API
  async getOverviewAnalytics(params?: { period?: string; timezone?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.timezone) queryParams.append('timezone', params.timezone);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/overview${queryString ? `?${queryString}` : ''}`);
  }

  async getTimeseriesAnalytics(params?: {
    period?: string;
    metric?: string;
    granularity?: string;
    timezone?: string
  }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.metric) queryParams.append('metric', params.metric);
    if (params?.granularity) queryParams.append('granularity', params.granularity);
    if (params?.timezone) queryParams.append('timezone', params.timezone);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/timeseries${queryString ? `?${queryString}` : ''}`);
  }

  async getOffersAnalytics(params?: {
    period?: string;
    limit?: number;
    sort_by?: string
  }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/offers${queryString ? `?${queryString}` : ''}`);
  }

  async getPublishersAnalytics(params?: {
    period?: string;
    limit?: number;
    sort_by?: string
  }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/publishers${queryString ? `?${queryString}` : ''}`);
  }

  async getGeoAnalytics(params?: {
    period?: string;
    metric?: string
  }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.metric) queryParams.append('metric', params.metric);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/geo${queryString ? `?${queryString}` : ''}`);
  }

  async getRealtimeAnalytics() {
    return this.request('/api/analytics/realtime');
  }

  async getDashboardAnalytics() {
    return this.request('/api/analytics/dashboard');
  }

  async getAdvertiserOffersReport(advertiserId: number) {
    return this.request(`/api/analytics/advertiser-offers/${advertiserId}`);
  }

  async getOfferAffiliatesReport(offerId: number) {
    return this.request(`/api/analytics/offer-affiliates/${offerId}`);
  }

  async getDailyReport(params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: string;
    limit?: number;
    page?: number;
    skip?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/daily-report${queryString ? `?${queryString}` : ''}`);
  }

  async getConversions(params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    advertiserId?: string;
    affiliateId?: string;
    advertiserIds?: string;
    affiliateIds?: string;
    goalType?: string;
    goalTypes?: string;
    limit?: number;
    page?: number;
    skip?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.status) queryParams.append('status', params.status);
    // Support both single and multiple IDs (backward compatible)
    if (params?.advertiserId) queryParams.append('advertiserId', params.advertiserId);
    if (params?.affiliateId) queryParams.append('affiliateId', params.affiliateId);
    if (params?.advertiserIds) queryParams.append('advertiserIds', params.advertiserIds);
    if (params?.affiliateIds) queryParams.append('affiliateIds', params.affiliateIds);
    if (params?.goalType) queryParams.append('goalType', params.goalType);
    if (params?.goalTypes) queryParams.append('goalTypes', params.goalTypes);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/conversions${queryString ? `?${queryString}` : ''}`);
  }

  async getGoalTypes() {
    return this.request('/api/goal-types');
  }

  async getConversionDetails(id: string) {
    return this.request(`/api/analytics/conversions/${id}`);
  }

  async firePostback(id: string) {
    return this.request(`/api/analytics/conversions/${id}/fire-postback`, {
      method: 'POST'
    });
  }

  async getTopOffers(params?: {
    period?: string;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/top-offers${queryString ? `?${queryString}` : ''}`);
  }

  async getCustomPayouts(params?: {
    status?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/custom-payouts${queryString ? `?${queryString}` : ''}`);
  }

  async getBilling(params?: {
    type?: string; // 'invoices' | 'payouts' | 'both'
  }) {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/billing${queryString ? `?${queryString}` : ''}`);
  }

  async getGoalReport(params?: { period?: string; search?: string; limit?: number; }) {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    return this.request(`/api/analytics/goal-report${queryString ? `?${queryString}` : ''}`);
  }

  // Offer-Publisher Assignment API
  async getOfferPublishers(offerId: number) {
    return this.request(`/api/offers/${offerId}/publishers`);
  }

  async getPublisherOffers(publisherId: number) {
    return this.request(`/api/publishers/${publisherId}/offers`);
  }

  async getPublisherAssignments(publisherId: number) {
    return this.request(`/api/publishers/${publisherId}/assignments`);
  }

  async getPublisherMetrics(publisherId: number) {
    return this.request(`/api/publishers/${publisherId}/metrics`, {
      headers: {} // No admin token needed for publisher metrics
    });
  }

  async getPublisherTodayOffersReport(publisherId: number) {
    return this.request(`/api/publishers/${publisherId}/today-offers-report`, {
      headers: {} // No admin token needed for publisher reports
    });
  }

  async getPublisherPerformance(publisherId: number, params?: {
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request(`/api/publishers/${publisherId}/performance${queryString ? `?${queryString}` : ''}`, {
      headers: {} // No admin token needed for publisher performance
    });
  }

  async getPublisherDateOffersReport(publisherId: number, date: string) {
    return this.request(`/api/publishers/${publisherId}/date-offers-report?date=${date}`, {
      headers: {} // No admin token needed for publisher reports
    });
  }

  async getPublisherEventReport(publisherId: number, params?: {
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request(`/api/publishers/${publisherId}/event-report${queryString ? `?${queryString}` : ''}`, {
      headers: {} // No admin token needed for publisher event report
    });
  }

  async assignPublisherToOffer(offerId: number, publisherId: number, data?: {
    status?: string;
    custom_payout?: string;
    notes?: string;
    daily_click_cap?: number;
    monthly_click_cap?: number;
    daily_conversion_cap?: number;
    monthly_conversion_cap?: number;
  }) {
    return this.request(`/api/offers/${offerId}/publishers/${publisherId}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async updateOfferPublisherAssignment(assignmentId: string, data: {
    status?: string;
    custom_payout?: string;
    notes?: string;
    daily_click_cap?: number;
    monthly_click_cap?: number;
    daily_conversion_cap?: number;
    monthly_conversion_cap?: number;
  }) {
    return this.request(`/api/offer-publishers/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removePublisherFromOffer(offerId: number, publisherId: number) {
    return this.request(`/api/offers/${offerId}/publishers/${publisherId}`, {
      method: 'DELETE',
    });
  }

  // D-LAB Integration API
  async syncDLabCampaigns() {
    return this.request('/api/admin/sync-dlab', {
      method: 'POST',
    });
  }

  async getDLabStatus() {
    return this.request('/api/admin/dlab-status');
  }
}

export const apiService = new ApiService();
